import { z } from 'zod';

// Super-admin data-management DTOs (snake_case wire contract). Staged edits to
// platform-maintained data (personas, company attributes) with accept-to-apply
// versioning. See packages/db/src/schema/data-management.ts and the plan.

export const PROPOSED_ENTITY_TYPES = [
  'persona',
  'company_enrichment',
  'agent',
] as const;
export type ProposedEntityType = (typeof PROPOSED_ENTITY_TYPES)[number];

export const CHANGE_KINDS = ['material', 'silent'] as const;
export type ChangeKind = (typeof CHANGE_KINDS)[number];

export const PROPOSED_CHANGE_STATUSES = [
  'open',
  'applied',
  'discarded',
  'superseded',
] as const;
export type ProposedChangeStatus = (typeof PROPOSED_CHANGE_STATUSES)[number];

export const proposedDataChangeSchema = z.object({
  id: z.string(),
  entity_type: z.enum(PROPOSED_ENTITY_TYPES),
  entity_key: z.string(),
  scope: z.string(),
  base_snapshot: z.record(z.string(), z.unknown()).nullable(),
  proposed: z.record(z.string(), z.unknown()),
  change_kind: z.enum(CHANGE_KINDS),
  status: z.enum(PROPOSED_CHANGE_STATUSES),
  proposed_by: z.string().nullable(),
  note: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProposedDataChange = z.infer<typeof proposedDataChangeSchema>;

export const proposedDataChangesListResponseSchema = z.object({
  changes: z.array(proposedDataChangeSchema),
});
export type ProposedDataChangesListResponse = z.infer<
  typeof proposedDataChangesListResponseSchema
>;

// Stage a change (super-admin). `apply_direct: true` bypasses staging + the
// accept-to-apply safety and writes live immediately — early-testing only,
// behind an explicit UI confirmation.
export const proposeDataChangeRequestSchema = z.object({
  entity_type: z.enum(PROPOSED_ENTITY_TYPES),
  entity_key: z.string().min(1).max(255),
  proposed: z.record(z.string(), z.unknown()),
  change_kind: z.enum(CHANGE_KINDS).default('material'),
  scope: z.string().max(64).default('global'),
  note: z.string().max(2000).optional(),
  apply_direct: z.boolean().optional(),
});
export type ProposeDataChangeRequest = z.infer<
  typeof proposeDataChangeRequestSchema
>;
